import { useState } from 'react';
import { getAuthToken } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '@/config/constants';
import { SparklesIcon, CheckIcon } from '@heroicons/react/24/outline';

interface Tag {
  id: string;
  name: string;
  slug: string;
  color: string;
}

interface SuggestedTag {
  name: string;
  isNew?: boolean;
}

interface AITagSuggestionsProps {
  title: string;
  content: string;
  category?: string;
  location?: string;
  existingTags: Tag[];
  selectedTagIds: string[];
  onTagToggle: (tagId: string) => void;
  onNewTagCreated: (tag: Tag) => void;
}

const AITagSuggestions = ({
  title,
  content,
  category,
  location,
  existingTags,
  selectedTagIds,
  onTagToggle,
  onNewTagCreated,
}: AITagSuggestionsProps) => {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedTag[]>([]);
  const [creatingTag, setCreatingTag] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const suggestTags = async () => {
    // Use content if available, otherwise fall back to title
    const promptText = content?.substring(0, 500) || title;
    if (!promptText) {
      setError('Add a title or content first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = getAuthToken();

      const response = await fetch(`${API_ENDPOINTS.ai}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: promptText,
          type: 'tags',
          context: {
            title,
            category,
            location,
            existingTags: existingTags.map(t => t.name),
          },
        }),
      });

      const result = await response.json();

      if (result.success && result.data?.tags) {
        setSuggestions(result.data.tags);
      } else {
        setError(result.error || 'Failed to suggest tags');
      }
    } catch (error) {
      console.error('AI tag suggestion error:', error);
      setError('Failed to suggest tags. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const findExistingTag = (name: string): Tag | undefined => {
    return existingTags.find(t => t.name.toLowerCase() === name.toLowerCase());
  };

  const handleTagClick = async (suggestion: SuggestedTag) => {
    const existing = findExistingTag(suggestion.name);

    if (existing) {
      onTagToggle(existing.id);
      return;
    }

    // New tag — create it first
    try {
      setCreatingTag(suggestion.name);
      const token = getAuthToken();

      const response = await fetch(API_ENDPOINTS.tags, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: suggestion.name }),
      });

      const result = await response.json();

      if (result.success) {
        const createdTag = result.data;
        onNewTagCreated(createdTag);
        onTagToggle(createdTag.id);
        // Mark as no longer new in suggestions
        setSuggestions(prev =>
          prev.map(s =>
            s.name === suggestion.name ? { ...s, isNew: false } : s
          )
        );
      }
    } catch (error) {
      console.error('Tag creation error:', error);
    } finally {
      setCreatingTag(null);
    }
  };

  const isSelected = (name: string): boolean => {
    const existing = findExistingTag(name);
    return existing ? selectedTagIds.includes(existing.id) : false;
  };

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={suggestTags}
        disabled={loading}
        className="inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gradient-to-r from-purple-600 to-violet-600 text-white hover:from-purple-700 hover:to-violet-700 disabled:opacity-50 transition-all"
      >
        {loading ? (
          <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent" />
        ) : (
          <SparklesIcon className="h-3 w-3" />
        )}
        <span>{loading ? 'Suggesting...' : 'Suggest Tags'}</span>
      </button>

      {error && <p className="text-xs text-red-500">{error}</p>}

      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {suggestions.map(suggestion => {
            const existing = findExistingTag(suggestion.name);
            const selected = isSelected(suggestion.name);
            const isCreating = creatingTag === suggestion.name;

            if (existing || !suggestion.isNew) {
              // Existing tag pill
              return (
                <button
                  key={suggestion.name}
                  type="button"
                  onClick={() => handleTagClick(suggestion)}
                  className={`inline-flex items-center space-x-1 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                    selected
                      ? 'bg-green-200 border-green-400 text-green-900'
                      : 'bg-green-100 border-green-300 text-green-800 hover:bg-green-200'
                  }`}
                >
                  {selected && <CheckIcon className="h-3 w-3" />}
                  <span>{suggestion.name}</span>
                </button>
              );
            }

            // New tag pill
            return (
              <button
                key={suggestion.name}
                type="button"
                onClick={() => handleTagClick(suggestion)}
                disabled={isCreating}
                className="inline-flex items-center space-x-1 px-2.5 py-1 text-xs font-medium rounded-full border border-dashed border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100 disabled:opacity-50 transition-colors"
              >
                {isCreating ? (
                  <div className="animate-spin rounded-full h-3 w-3 border border-violet-600 border-t-transparent" />
                ) : (
                  <span>+</span>
                )}
                <span>{isCreating ? 'creating...' : suggestion.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AITagSuggestions;
